import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import api from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import axios from "axios";
import { extractApiErrorMessage } from "../../../utils/apiError";
import { downloadFile } from "../../../utils/downloadUtils";

// ==================== STATE ====================
interface EmailDetailItem {
  commid?: string | number;
  applicationid?: string | number;
  subject?: string;
  emailtype?: string;
  emaildate?: string;
  activeflag?: string;
  createdby?: string;
  createdtime?: string;
  lastupdatedby?: string;
  lastupdatedtime?: string;
  content?: string;
  body?: string;
  attachments?: Array<{
    id?: string | number;
    fileName?: string;
    name?: string;
  }>;
}

interface TuitionAdminState {
  applications: any[];
  statusOptions: string[];
  activeStatusOptions: string[];
  regionOptions: any[];
  unionOptions: any[];
  emailOptions: {
    emails: Array<{
      id: number;
      code: string;
      value: string;
    }>;
  } | null;
  commentTypeOptions: string[];
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  documents: any[];
  emails: any[];
  comments: any[];
  emailDetails: EmailDetailItem | null;
  loading: boolean;
  error: string | null;
  patchCommentError: string | null;
  applicationDetails: any;
  reimbursementCourses: any[];
  courseDetails: any;
  balances: any;
  balancesLoading: boolean;
  balancesError: string | null;
  balancesPatchError: string | null;
  employeeDetails: any;
  employeeDetailsLoading: boolean;
  employeeDetailsError: string | null;
  isEligible: boolean; // Added eligibility state
  eligibilityReason: string | null;
  applicationId?: string | null;
}

const initialState: TuitionAdminState = {
  applications: [],
  statusOptions: [],
  activeStatusOptions: [],
  regionOptions: [],
  unionOptions: [],
  emailOptions: null,
  commentTypeOptions: [],
  loading: false,
  error: null,
  patchCommentError: null,
  totalRecords: 0,
  currentPage: 1,
  pageSize: 10,
  totalPages: 0,
  documents: [],
  emails: [],
  comments: [],
  emailDetails: null,
  reimbursementCourses: [],
  courseDetails: null,
  applicationDetails: null,
  balances: null,
  balancesLoading: false,
  balancesError: null,
  balancesPatchError: null,
  employeeDetails: null,
  employeeDetailsLoading: false,
  employeeDetailsError: null,
  isEligible: false,
  eligibilityReason: null,
  applicationId: null,
};

// ==================== THUNKS ====================
// Helper function for handling API errors with response data
const handleApiError = (error: unknown, fallbackMessage: string) => {
  return (error as any)?.response
    ? extractApiErrorMessage(error, fallbackMessage)
    : fallbackMessage;
};

// Async thunk for fetching status options
export const fetchStatusOptions = createAsyncThunk(
  "tuitionAdmin/fetchStatusOptions",
  async (applType: string, { rejectWithValue }) => {
    try {
      const response = await api.get(
        endpoints.tuition.admin.getStatusOptions(applType),
      );
      if (response.data && Array.isArray(response.data)) {
        return response.data.map(
          (item: any) => item.statusName || item.name || item.value || item,
        );
      }
      return [];
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Failed to fetch status options"));
    }
  },
);

// Async thunk for fetching active status options (for Change Status action)
export const fetchActiveStatusOptions = createAsyncThunk(
  "tuitionAdmin/fetchActiveStatusOptions",
  async (applType: string, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `${endpoints.tuition.admin.getStatusOptions(applType)}&isActive=Y`,
      );
      if (response.data && Array.isArray(response.data)) {
        return response.data.map(
          (item: any) => item.statusName || item.name || item.value || item,
        );
      }
      return [];
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Failed to fetch active status options"));
    }
  },
);

// Async thunk for fetching email options
export const fetchEmailOptions = createAsyncThunk(
  "tuitionAdmin/fetchEmailOptions",
  async (applType: string, { rejectWithValue }) => {
    try {
      const response = await api.get(
        endpoints.tuition.admin.getTuitionOptions("email", applType),
      );
      console.log("Email options response:", response.data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const responseData = (error as any)?.response?.data;
        return rejectWithValue(
          responseData &&
            (typeof responseData === "string"
              ? responseData.length > 0
              : Object.keys(responseData).length > 0)
            ? extractApiErrorMessage(error, "Failed to fetch email options")
            : "Failed to fetch email options",
        );
      }
      return rejectWithValue("An unexpected error occurred");
    }
  },
);

// Async thunk for fetching tuition options (regions and unions)
export const fetchTuitionOptions = createAsyncThunk(
  "tuitionAdmin/fetchTuitionOptions",
  async (
    { type, applType }: { type: string; applType: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get(
        endpoints.tuition.admin.getTuitionOptions(type, applType),
      );
      return response.data || {};
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response
      ) {
        return rejectWithValue((error as any).response.data);
      }
      return rejectWithValue("Failed to fetch tuition options");
    }
  },
);

// Fetch comment type options for Tuition Admin
export const fetchCommentTypeOptions = createAsyncThunk(
  "tuitionAdmin/fetchCommentTypeOptions",
  async (applType: string, { rejectWithValue }) => {
    try {
      const response = await api.get(
        endpoints.tuition.admin.getTuitionOptions("commentType", applType),
      );
      const data = response.data;
      if (Array.isArray(data)) {
        return data.map((item: any) => item?.name || item?.value || item);
      }
      if (data?.commentTypes && Array.isArray(data.commentTypes)) {
        return data.commentTypes.map(
          (item: any) => item?.name || item?.value || item,
        );
      }
      return [];
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        (error as any).response &&
        typeof (error as any).response === "object" &&
        "data" in (error as any).response
      ) {
        return rejectWithValue((error as any).response.data);
      }
      return rejectWithValue("Failed to fetch comment type options");
    }
  },
);

// Async thunk for searching tuition applications
export const searchTuitionApplications = createAsyncThunk(
  "tuitionAdmin/searchApplications",
  async (
    params: {
      applType: string;
      page?: number;
      pageSize?: number;
      status?: string;
      courseStartDate?: string;
      courseEndDate?: string;
      submissionStartDate?: string;
      submissionEndDate?: string;
      eeStartDate?: string;
      eeEndDate?: string;
      selectedField?: string;
      selectedFieldValue?: string;
      lastNameStart?: string;
      lastNameEnd?: string;
      regionId?: string;
      unionId?: string;
      sortOrder?: string;
      sortBy?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get(
        endpoints.tuition.admin.searchApplications,
        {
          params,
        },
      );
      return {
        data: response.data?.data || [],
        total: response.data?.total || 0,
        page: response.data?.page || 1,
        pageSize: response.data?.pageSize || 10,
        totalPages: response.data?.totalPages || 0,
      };
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response
      ) {
        // @ts-ignore
        return rejectWithValue((error as any).response.data);
      }
      return rejectWithValue("Failed to search tuition applications");
    }
  },
);

// Fetch documents for Tuition Admin
export const getTuitionAdminDocuments = createAsyncThunk(
  "tuitionAdmin/getDocuments",
  async (
    { applicationId, nuid }: { applicationId: string; nuid?: string },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.getDocuments(applicationId);
      const response = await api.get(url, {
        headers: { "x-emp-nuid": nuid },
      });
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data || error?.message || "Failed to fetch documents";
      return rejectWithValue(message);
    }
  },
);
// Fetch a single email's details for Tuition Admin
export const getTuitionAdminEmailDetails = createAsyncThunk(
  "tuitionAdmin/getEmailDetails",
  async (
    {
      applicationId,
      commId,
      nuid,
    }: { applicationId: string; commId: string; nuid?: string },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.getEmailDetails(
        applicationId,
        commId,
      );
      const response = await api.get(url, {
        headers: nuid ? { "x-emp-nuid": nuid } : undefined,
      });
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data ||
        error?.message ||
        "Failed to fetch email details";
      return rejectWithValue(message);
    }
  },
);
// Fetch emails for Tuition Admin
export const getTuitionAdminEmails = createAsyncThunk(
  "tuitionAdmin/getEmails",
  async (
    { applicationId, nuid }: { applicationId: string; nuid?: string },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.getEmails(applicationId);
      const response = await api.get(url, {
        headers: nuid ? { "x-emp-nuid": nuid } : undefined,
      });
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data || error?.message || "Failed to fetch emails";
      return rejectWithValue(message);
    }
  },
);

export const uploadDocumentTuition = createAsyncThunk(
  "viewDocument/uploadDocument",
  async (
    {
      applicationId,
      file,
      description,
      nuid,
    }: {
      applicationId: string;
      file: File;
      description: string;
      nuid?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const documents = new FormData();
      documents.append("file", file);
      documents.append("description", description);

      await api.post(
        endpoints.tuition.admin.uploadDocument(applicationId),
        documents,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-emp-nuid": nuid,
          },
        },
      );

      return {
        applicationId,
        description,
        fileName: file.name,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to upload document");
    }
  },
);
// Bulk upload/set EE flag for given application IDs
export const patchTuitionAdminUploadEeFlag = createAsyncThunk(
  "tuitionAdmin/uploadEeFlag",
  async (
    { applicationIds }: { applicationIds: string[] },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.updateFlagEE;
      const payload = { applicationIds };
      const response = await api.patch(url, payload);
      return response.data ?? payload;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to upload EE flag",
      );
    }
  },
);
export const downloadDocumentAdmin = createAsyncThunk(
  "admin/downloadDocument",
  async (
    {
      applicationId,
      documentId,
      fileName,
      nuid,
    }: {
      applicationId: string;
      documentId: string;
      fileName?: string;
      nuid: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.downloadDocument(
        applicationId,
        documentId,
      );
      const response = await api.get(url, {
        responseType: "blob",
        headers: {
          "x-emp-nuid": nuid,
        },
      });

      downloadFile({
        response,
        documentId,
        fileName,
        defaultFileName: "document"
      });

      return { applicationId, documentId };
    } catch (error: any) {
      // When responseType is 'blob', error responses are also blobs
      // We need to read the blob and parse it as JSON to get the error message
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsedData = JSON.parse(text);
          error.response.data = parsedData;
        } catch (parseError) {
          console.log("Failed to parse error response blob:", parseError);
        }
      }

      const errorMessage = extractApiErrorMessage(
        error,
        "Failed to download document",
      );
      return rejectWithValue(errorMessage);
    }
  },
);
// Patch edit document (admin edits a specific document)
export const patchTuitionAdminEditDocument = createAsyncThunk(
  "tuitionAdmin/editDocument",
  async (
    {
      applicationId,
      documentId,
      data,
      nuid,
    }: {
      applicationId: string;
      documentId: string;
      data: { description: string; eeFlag: boolean; file?: any };
      nuid?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.editDocuments(
        applicationId,
        documentId,
      );
      const response = await api.patch(url, data, {
        headers: nuid ? { "x-emp-nuid": nuid } : undefined,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to edit document",
      );
    }
  },
);
// Patch verify (admin confirms verification flags for an application)
export const patchTuitionAdminVerify = createAsyncThunk(
  "tuitionAdmin/patchVerify",
  async (
    {
      applicationId,
      data,
      nuid,
    }: {
      applicationId: string;
      data: {
        gradesVerified?: "Y" | "N";
        paystubVerified?: "Y" | "N";
        receiptVerified?: "Y" | "N";
        scheduleVerified?: "Y" | "N";
        verified5?: "Y" | "N";
        verified6?: "Y" | "N";
        [key: string]: any;
      };
      nuid?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.patchVerify(applicationId);
      const response = await api.patch(url, data ?? {}, {
        headers: nuid ? { "x-emp-nuid": nuid } : undefined,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to verify application",
      );
    }
  },
);
// Bulk status update for Tuition Admin applications
export const patchTuitionAdminBulkStatus = createAsyncThunk(
  "tuitionAdmin/bulkStatusUpdate",
  async (
    {
      applicationIds,
      newStatus,
    }: { applicationIds: string[]; newStatus: string },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.bulkStatusUpdate;
      const payload = { applicationIds, newStatus };
      const response = await api.patch(url, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to update status in bulk"),
      );
    }
  },
);
// Fetch comments for Tuition Admin
export const getTuitionAdminComments = createAsyncThunk(
  "tuitionAdmin/getComments",
  async (
    { applicationId, nuid }: { applicationId: string; nuid?: string },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.getComments(applicationId);
      const response = await api.get(url, {
        headers: nuid ? { "x-emp-nuid": nuid } : undefined,
      });
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data || error?.message || "Failed to fetch comments";
      return rejectWithValue(message);
    }
  },
);

// Save a new comment for Tuition Admin
export const saveTuitionAdminComment = createAsyncThunk(
  "tuitionAdmin/saveComment",
  async (
    {
      applicationId,
      data,
      nuid,
    }: {
      applicationId: string;
      data: { commentType: string; commentDate: string; commentText: string };
      nuid?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.saveComment(applicationId);
      const response = await api.post(url, data, {
        headers: nuid ? { "x-emp-nuid": nuid } : undefined,
      });
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data || error?.message || "Failed to save comment";
      return rejectWithValue(message);
    }
  },
);

// Download attachment for Tuition Admin emails (similar to document download)
export const downloadTuitionAdminAttachment = createAsyncThunk(
  "tuitionAdmin/downloadAttachment",
  async (
    {
      applicationId,
      documentId,
      fileName,
      nuid,
    }: {
      applicationId: string;
      documentId: string;
      fileName?: string;
      nuid?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.downloadAttachment(
        applicationId,
        documentId,
      );
      const response = await api.get(url, {
        responseType: "blob",
        headers: nuid ? { "x-emp-nuid": nuid } : undefined,
      });

      downloadFile({
        response,
        documentId,
        fileName,
        defaultFileName: "attachment"
      });

      return { applicationId, documentId };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to download attachment");
    }
  },
);
// Patch (update) an existing comment for Tuition Admin
export const patchTuitionAdminEditComment = createAsyncThunk(
  "tuitionAdmin/patchEditedComment",
  async (
    {
      applicationId,
      commentId,
      data,
      nuid,
    }: {
      applicationId: string;
      commentId: string;
      data: { commentText: string };
      nuid?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.saveEditedComment(
        applicationId,
        commentId,
      );
      const response = await api.patch(url, data, {
        headers: nuid ? { "x-emp-nuid": nuid } : undefined,
      });
      // Return both the id and the updated payload for reducer to merge cleanly
      return { commentId, dataSent: data, updated: response.data };
    } catch (error: any) {
      const message =
        error?.response?.data || error?.message || "Failed to update comment";
      return rejectWithValue(extractApiErrorMessage(error, message));
    }
  },
);
export const fetchAllApplicationDetails = createAsyncThunk(
  "tuitionAdmin/fetchAllApplicationDetails",
  async (
    { applicationId, nuid }: { applicationId: string; nuid?: string },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.allApplicationDetails(applicationId);
      const response = await api.get(url, {
        headers: { "x-emp-nuid": nuid || "" },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to fetch application details"),
      );
    }
  },
);

// Update application details for Tuition Admin
export const updateAdminApplicationDetails = createAsyncThunk(
  "tuitionAdmin/updateApplication",
  async (
    {
      applicationId,
      data,
      nuid,
    }: {
      applicationId: string;
      data: any;
      nuid?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      console.log("=== updateAdminApplicationDetails Thunk Start ===");
      console.log("applicationId:", applicationId);
      console.log("nuid:", nuid);
      console.log("data payload type:", typeof data);
      console.log("data payload value:", data);
      console.log("data is null:", data === null);
      console.log("data is undefined:", data === undefined);

      if (!data || typeof data !== "object") {
        console.warn("⚠ Warning: data is not a valid object");
        console.log("data:", data);
      }

      const url =
        endpoints.tuition.admin.updateApplicationDetails(applicationId);
      console.log("API URL:", url);
      console.log("Headers:", { "x-emp-nuid": nuid || "(empty)" });
      console.log("Request body:", JSON.stringify(data, null, 2));

      const response = await api.patch(url, data, {
        headers: { "x-emp-nuid": nuid || "" },
      });

      console.log("✓ API Response Status:", response.status);
      console.log(
        "✓ API Response Data:",
        JSON.stringify(response.data, null, 2),
      );
      console.log("=== updateAdminApplicationDetails Thunk End (Success) ===");

      return response.data;
    } catch (error: any) {
      console.log("=== updateAdminApplicationDetails Thunk Error ===");
      console.error("✗ Error occurred");
      console.error("Error object:", error);
      console.error("Error status:", error?.response?.status);
      console.error("Error response data:", error?.response?.data);
      console.error("Error message:", error?.message);
      console.error("Error code:", error?.code);

      const message =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        "Failed to update application";

      console.log("Rejecting with message:", message);
      console.log("=== updateAdminApplicationDetails Thunk Error End ===");
      return rejectWithValue(message);
    }
  },
);

// Fetch balance info for Tuition Admin
export const getBalanceInfo = createAsyncThunk(
  "tuitionAdmin/getBalanceInfo",
  async (
    { applicationId, nuid }: { applicationId: string; nuid: string },
    { rejectWithValue },
  ) => {
    try {
      if (!nuid) {
        console.error("WARNING: nuid is empty!");
      }
      const response = await api.get(
        endpoints.tuition.admin.getBalances(applicationId),
        {
          headers: {
            "x-emp-nuid": nuid,
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to fetch balance information"),
      );
    }
  },
);

// Patch (edit) balances for Tuition Admin
export const editBalanceDetails = createAsyncThunk(
  "tuitionAdmin/editBalances",
  async (
    {
      applicationId,
      data,
    }: { applicationId: string; data: any; nuid?: string },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.editBalances(applicationId);
      const response = await api.patch(url, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to edit balances"),
      );
    }
  },
);

// Fetch reimbursement courses list for Tuition Admin
export const fetchReimbursementCoursesList = createAsyncThunk(
  "tuitionAdmin/fetchReimbursementCourses",
  async (
    {
      applicationId,
      nuid,
    }: {
      applicationId: string;
      nuid?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.reimbursementCourses(applicationId);
      const empNuid = nuid;
      const response = await api.get(url, {
        headers: {
          "x-emp-nuid": empNuid,
        },
      });
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data ||
        error?.message ||
        "Failed to fetch reimbursement courses";
      return rejectWithValue(message);
    }
  },
);

// Fetch reimbursement course details for Tuition Admin
export const fetchReimbursementCourseDetails = createAsyncThunk(
  "tuitionAdmin/fetchCourseDetails",
  async (
    {
      applicationId,
      empCourseId,
      nuid,
    }: {
      applicationId: string;
      empCourseId: string | number;
      nuid?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.reimbursementCourseDetails(
        applicationId,
        String(empCourseId),
      );
      const empNuid = nuid;
      const response = await api.get(url, {
        headers: {
          "x-emp-nuid": empNuid,
        },
      });
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data ||
        error?.message ||
        "Failed to fetch course details";
      return rejectWithValue(message);
    }
  },
);

// Update reimbursement course details for Tuition Admin
export const updateReimbursementDetails = createAsyncThunk(
  "tuitionAdmin/updateReimbursementDetails",
  async (
    {
      applicationId,
      empCourseId,
      data,
      nuid,
    }: {
      applicationId: string;
      empCourseId: string | number;
      data: any;
      nuid?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const url = endpoints.tuition.admin.reimbursementCourseDetails(
        applicationId,
        String(empCourseId),
      );
      const empNuid = nuid || sessionStorage.getItem("userId") || "";
      const response = await api.patch(url, data, {
        headers: {
          "x-emp-nuid": empNuid,
        },
      });
      return response.data;
    } catch (error: any) {
      // Try to get message from response
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update course details";
      console.error("Message being rejected:", message);

      return rejectWithValue(message);
    }
  },
);

// Fetch employee details for Admin by NUID
export const fetchEmployeeDetailsAdmin = createAsyncThunk(
  "tuitionAdmin/fetchEmployeeDetails",
  async ({ empId }: { empId: string }, { rejectWithValue }) => {
    try {
      const url = endpoints.tuition.admin.getEmployeeDetails;
      // Server expects empId as a request parameter, not a header
      const response = await api.get(url, {
        params: { empId },
      });
      return response.data;
    } catch (error: any) {
      const message =
        error?.response?.data ||
        error?.message ||
        "Failed to fetch employee details";
      return rejectWithValue(message);
    }
  },
);

// Submit application details (Admin) - requires x-emp-nuid in headers
export const submitApplicationDetailsAdmin = createAsyncThunk(
  "tuitionAdmin/submitApplicationDetailsAdmin",
  async (
    { payload, nuid }: { payload: Record<string, any>; nuid?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(
        endpoints.tuition.admin.submitApplicationDetails,
        payload,
        {
          headers: { "x-emp-nuid": nuid || "" },
        },
      );
      return response.data;
    } catch (error: any) {
      console.error("Error submitting application details:", error);
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to submit application details"),
      );
    }
  },
);

export const saveTuitionEmployeeDetailsAdmin = createAsyncThunk<
  any,
  {
    data: {
      altPhone?: number;
      prefPhone?: string;
      homeEmail?: string;
      prefEmail?: string;
    };
    nuid?: string;
  },
  { rejectValue: string }
>(
  "tuition/saveTuitionEmployeeDetailsAdmin",
  async ({ data, nuid }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        endpoints.tuition.admin.saveApplicantInfo,
        data,
        {
          headers: {
            "x-emp-nuid": nuid || "",
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to update employee details"),
      );
    }
  },
);

export const fetchClassEligibilityAdmin = createAsyncThunk<
  { isEligible: boolean; reason: string | null },
  { courseStartDate: string; nuid?: string },
  { rejectValue: string }
>(
  "tuition/fetchClassEligibilityAdmin",
  async ({ courseStartDate, nuid }, { rejectWithValue }) => {
    try {
      const url = `${endpoints.tuition.admin.classInfoEligibility}?applType=AT001&courseStartDate=${courseStartDate}`;
      const response = await api.get(url, {
        headers: {
          "x-emp-nuid": nuid || "",
        },
      });
      return { isEligible: response.data.eligible, reason: null };
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(
          error,
          "An error occurred while checking class eligibility",
        ),
      );
    }
  },
);
export const fetchMilesDataAdmin = createAsyncThunk<
  any,
  { courseStartDate: string; totalMiles: number; nuid: string },
  { rejectValue: string }
>(
  "tuition/fetchMilesDataAdmin",
  async ({ courseStartDate, totalMiles, nuid }, { rejectWithValue }) => {
    try {
      const url = `${endpoints.tuition.admin.classInfoMileRate}?courseStartDate=${courseStartDate}&totalMiles=${totalMiles}`;
      const response = await api.get(url, {
        headers: {
          "x-emp-nuid": nuid || "",
        },
      });
      return response.data;
    } catch (error: any) {
      if (error?.message) {
        let errorMessage = error.message;
        console.log("error", errorMessage);
      }
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to fetch miles data"),
      );
    }
  },
);

export const updateManagerDetailsAdmin = createAsyncThunk<
  any,
  {
    data: {
      managerPhone?: string;
      altManagerPhone?: string;
    };
    nuid?: string;
  },
  { rejectValue: string }
>(
  "tuition/updateManagerDetailsAdmin",
  async ({ data, nuid }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        endpoints.tuition.admin.saveManagerInfo,
        data,
        {
          headers: {
            "x-emp-nuid": nuid || "",
          },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response
          ? extractApiErrorMessage(error, "Failed to update manager details")
          : error?.message || "Failed to update manager details",
      );
    }
  },
);
export const downloadPrintApplication = createAsyncThunk<
  any,
  { applicationId: string; nuid?: string },
  { rejectValue: string }
>(
  "viewDocument/downloadPrintApplication",
  async ({ applicationId, nuid }, { rejectWithValue, signal }) => {
    try {
      const url = endpoints.tuition.admin.printApplication(applicationId);
      const response = await api.get(url, {
        signal,
        headers: {
          "x-emp-nuid": nuid || "",
        },
      });

      const htmlContent = response.data?.htmlContent;
      if (!htmlContent) {
        return rejectWithValue("No HTML content received from backend");
      }

      const printWindow = window.open("about:blank", "_blank");
      if (printWindow) {
        // Create a minimal document in the popup and set the HTML content safely
        printWindow.document.open();
        printWindow.document.close();
        // if body exists, set its innerHTML, otherwise create a body element
        if (printWindow.document.body) {
          printWindow.document.body.innerHTML = htmlContent;
        } else {
          const bodyEl = printWindow.document.createElement("body");
          bodyEl.innerHTML = htmlContent;
          // append to documentElement if present, else to document
          if (printWindow.document.documentElement) {
            printWindow.document.documentElement.appendChild(bodyEl);
          } else {
            printWindow.document.appendChild(bodyEl as any);
          }
        }
        printWindow.focus();
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
        }, 50);
      } else {
        alert(
          "Please allow pop-ups for this site to view the content in a new tab.",
        );
      }
      return { applicationId };
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled"
      ) {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(
        error?.message || "Failed to fetch print application details",
      );
    }
  },
);

export const downloadPrintFaxCover = createAsyncThunk<
  any,
  { applicationId: string; nuid?: string },
  { rejectValue: string }
>(
  "viewDocument/downloadPrintFaxCover",
  async ({ applicationId, nuid }, { rejectWithValue, signal }) => {
    try {
      const url = endpoints.tuition.admin.printFaxCover(applicationId);
      const response = await api.get(url, {
        signal,
        headers: { "x-emp-nuid": nuid || "" },
      });

      const htmlContent = response.data?.htmlContent;
      if (!htmlContent) {
        return rejectWithValue("No HTML content received from backend");
      }

      const printWindow = window.open("about:blank", "_blank");
      if (printWindow) {
        // Create a minimal document in the popup and set the HTML content safely
        printWindow.document.open();
        printWindow.document.close();
        // if body exists, set its innerHTML, otherwise create a body element
        if (printWindow.document.body) {
          printWindow.document.body.innerHTML = htmlContent;
        } else {
          const bodyEl = printWindow.document.createElement("body");
          bodyEl.innerHTML = htmlContent;
          // append to documentElement if present, else to document
          if (printWindow.document.documentElement) {
            printWindow.document.documentElement.appendChild(bodyEl);
          } else {
            printWindow.document.appendChild(bodyEl as any);
          }
        }
        printWindow.focus();
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
        }, 50);
      } else {
        alert(
          "Please allow pop-ups for this site to view the content in a new tab.",
        );
      }
      return { applicationId };
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled"
      ) {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(
        error?.message || "Failed to fetch or generate fax cover PDF",
      );
    }
  },
);

// ==================== SLICE ====================
const tuitionAdminSlice = createSlice({
  name: "tuitionAdmin",
  initialState,
  reducers: {
    clearApplications(state) {
      state.applications = [];
      state.error = null;
    },
    clearAdminError(state) {
      state.error = null;
    },
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    clearTuitionAdminDocuments(state) {
      state.documents = [];
    },
    clearTuitionAdminEmails(state) {
      state.emails = [];
    },
    clearTuitionAdminComments(state) {
      state.comments = [];
    },
    clearTuitionAdminEmailDetails(state) {
      state.emailDetails = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Search applications
      .addCase(searchTuitionApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchTuitionApplications.fulfilled, (state, action) => {
        console.log("=== Redux searchTuitionApplications.fulfilled ===");
        console.log("Payload:", action.payload);
        state.loading = false;
        state.applications = action.payload.data;
        state.totalRecords = action.payload.total;
        state.currentPage = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.totalPages = action.payload.totalPages;
        console.log(
          "Redux state updated - applications count:",
          state.applications.length,
        );
        console.log("Redux state - currentPage:", state.currentPage);
      })
      .addCase(searchTuitionApplications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch all application details
      .addCase(fetchAllApplicationDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllApplicationDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.applicationDetails = action.payload;
      })
      .addCase(fetchAllApplicationDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update application details
      .addCase(updateAdminApplicationDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminApplicationDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.applicationDetails = action.payload;
      })
      .addCase(updateAdminApplicationDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch status options
      .addCase(fetchStatusOptions.fulfilled, (state, action) => {
        state.statusOptions = action.payload;
      })
      .addCase(fetchStatusOptions.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Fetch active status options
      .addCase(fetchActiveStatusOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveStatusOptions.fulfilled, (state, action) => {
        state.loading = false;
        state.activeStatusOptions = action.payload;
      })
      .addCase(fetchActiveStatusOptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch tuition options
      .addCase(fetchTuitionOptions.fulfilled, (state, action) => {
        if (action.payload.regions && Array.isArray(action.payload.regions)) {
          state.regionOptions = action.payload.regions;
        }
        if (action.payload.unions && Array.isArray(action.payload.unions)) {
          state.unionOptions = action.payload.unions;
        }
        // When fieldName (type) is 'comment', store options for the Comments tab
        const reqType = (action as any)?.meta?.arg?.type;
        if (String(reqType) === "comment") {
          const p = action.payload;
          let options: string[] = [];
          if (p?.comments && Array.isArray(p.comments)) {
            options = p.comments.map(
              (item: any) => item?.name || item?.value || item,
            );
          }
          state.commentTypeOptions = options;
        }
      })
      .addCase(fetchTuitionOptions.rejected, (state, action) => {
        state.error = action.payload as string;
      });
    // Email options
    builder
      .addCase(fetchEmailOptions.fulfilled, (state, action) => {
        state.emailOptions = action.payload;
      })
      .addCase(fetchEmailOptions.rejected, (state, action) => {
        state.error = action.payload as string;
      });
    builder
      .addCase(
        fetchCommentTypeOptions.fulfilled,
        (state, action: PayloadAction<string[]>) => {
          state.commentTypeOptions = action.payload ?? [];
        },
      )
      .addCase(fetchCommentTypeOptions.rejected, (state, action) => {
        state.error =
          (action.payload as string) || "Failed to fetch comment type options";
      });
    builder
      .addCase(getTuitionAdminDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getTuitionAdminDocuments.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.loading = false;
          state.documents = action.payload;
        },
      )
      .addCase(getTuitionAdminDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch documents";
      });

    builder.addCase(uploadDocumentTuition.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(uploadDocumentTuition.fulfilled, (state, action) => {
      state.loading = false;
      console.log("Document uploaded successfully:", action.payload);
    });
    builder.addCase(uploadDocumentTuition.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder
      .addCase(downloadDocumentAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadDocumentAdmin.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadDocumentAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    // Bulk EE flag upload
    builder.addCase(patchTuitionAdminUploadEeFlag.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(patchTuitionAdminUploadEeFlag.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(patchTuitionAdminUploadEeFlag.rejected, (state, action) => {
      state.loading = false;
      state.error =
        typeof action.payload === "string"
          ? action.payload
          : (action.error?.message ?? null);
    });
    builder.addCase(patchTuitionAdminEditDocument.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(patchTuitionAdminEditDocument.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(patchTuitionAdminEditDocument.rejected, (state, action) => {
      state.loading = false;
      state.error =
        typeof action.payload === "string"
          ? action.payload
          : (action.error?.message ?? null);
    });
    builder.addCase(patchTuitionAdminVerify.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(patchTuitionAdminVerify.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(patchTuitionAdminVerify.rejected, (state, action) => {
      state.loading = false;
      state.error =
        typeof action.payload === "string"
          ? action.payload
          : (action.error?.message ?? null);
    });
    // Bulk status update
    builder.addCase(patchTuitionAdminBulkStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(patchTuitionAdminBulkStatus.fulfilled, (state) => {
      state.loading = false;
      // Optional: update local application list statuses here if needed
      // Prefer refetching applications to ensure backend is the source of truth
    });
    builder.addCase(patchTuitionAdminBulkStatus.rejected, (state, action) => {
      state.loading = false;
      state.error =
        typeof action.payload === "string"
          ? action.payload
          : (action.error?.message ?? null);
    });
    builder
      .addCase(getTuitionAdminEmails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getTuitionAdminEmails.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.loading = false;
          state.emails = action.payload ?? [];
        },
      )
      .addCase(getTuitionAdminEmails.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch emails";
      });

    builder
      .addCase(getTuitionAdminComments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getTuitionAdminComments.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.loading = false;
          state.comments = action.payload ?? [];
        },
      )
      .addCase(getTuitionAdminComments.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch comments";
      });

    builder
      .addCase(getTuitionAdminEmailDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getTuitionAdminEmailDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.emailDetails = action.payload ?? null;
        },
      )
      .addCase(getTuitionAdminEmailDetails.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch email details";
      });

    builder
      .addCase(fetchReimbursementCourseDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchReimbursementCourseDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.courseDetails = action.payload ?? null;
        },
      )
      .addCase(fetchReimbursementCourseDetails.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch course details";
      });

    builder
      .addCase(updateReimbursementDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateReimbursementDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.courseDetails = action.payload ?? null;
        },
      )
      .addCase(updateReimbursementDetails.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to update course details";
      });

    builder
      .addCase(fetchReimbursementCoursesList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchReimbursementCoursesList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.reimbursementCourses = action.payload ?? [];
        },
      )
      .addCase(fetchReimbursementCoursesList.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch reimbursement courses";
      });

    // Download attachment
    builder
      .addCase(downloadTuitionAdminAttachment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadTuitionAdminAttachment.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadTuitionAdminAttachment.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to download attachment";
      });

    builder
      .addCase(saveTuitionAdminComment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        saveTuitionAdminComment.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          // Optionally append the saved comment to the list if payload contains it
          const saved = action.payload;
          const list = state.comments;
          if (Array.isArray(list) && saved && typeof saved === "object") {
            // Mark newly added comments as editable in UI
            list.push({ ...saved, __isNew: true });
          }
        },
      )
      .addCase(saveTuitionAdminComment.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to save comment";
      });

    builder
      .addCase(patchTuitionAdminEditComment.pending, (state) => {
        state.loading = true;
        state.patchCommentError = null;
      })
      .addCase(
        patchTuitionAdminEditComment.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.patchCommentError = null;

          const { commentId, dataSent, updated } = action.payload || {};
          if (!commentId || !Array.isArray(state.comments)) return;

          const index = state.comments.findIndex(
            (c: any) => String(c.commentId) === String(commentId),
          );

          if (index !== -1) {
            if (updated && typeof updated === "object") {
              state.comments[index] = {
                ...state.comments[index],
                ...updated,
                comments:
                  updated?.comments ??
                  updated?.commentText ??
                  dataSent?.commentText ??
                  state.comments[index].comments,
                lastUpdatedTime:
                  updated?.lastUpdatedTime ?? new Date().toISOString(),
              };
            } else {
              state.comments[index] = {
                ...state.comments[index],
                comments:
                  dataSent?.commentText ?? state.comments[index].comments,
                lastUpdatedTime: new Date().toISOString(),
              };
            }
          }
        },
      )

      .addCase(patchTuitionAdminEditComment.rejected, (state, action) => {
        state.loading = false;
        state.patchCommentError =
          (action.payload as string) || "Failed to update comment";
      });

    builder
      .addCase(getBalanceInfo.pending, (state) => {
        state.balancesLoading = true;
        state.balancesError = null;
      })
      .addCase(getBalanceInfo.fulfilled, (state, action) => {
        state.balancesLoading = false;
        state.balances = action.payload;
      })
      .addCase(getBalanceInfo.rejected, (state, action) => {
        state.balancesLoading = false;
        state.balancesError = action.payload as string;
      })
      .addCase(editBalanceDetails.pending, (state) => {
        state.balancesLoading = true;
        state.balancesPatchError = null;
      })
      .addCase(editBalanceDetails.fulfilled, (state, action) => {
        state.balancesLoading = false;
        // update balances with returned payload
        state.balances = action.payload ?? state.balances;
      })
      .addCase(editBalanceDetails.rejected, (state, action) => {
        state.balancesLoading = false;
        state.balancesPatchError = action.payload as string;
      })
      // Submit application details (Admin)
      .addCase(submitApplicationDetailsAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitApplicationDetailsAdmin.fulfilled, (state, action) => {
        state.loading = false;
        // Set applicationId in state for success banner
        if (action.payload?.applicationId) {
          state.applicationId = action.payload.applicationId;
        }
      })
      .addCase(submitApplicationDetailsAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to submit application details";
      })
      // Employee details (Admin)
      .addCase(fetchEmployeeDetailsAdmin.pending, (state) => {
        state.employeeDetailsLoading = true;
        state.employeeDetailsError = null;
      })
      .addCase(fetchEmployeeDetailsAdmin.fulfilled, (state, action) => {
        state.employeeDetailsLoading = false;
        state.employeeDetails = action.payload ?? null;
      })
      .addCase(fetchEmployeeDetailsAdmin.rejected, (state, action) => {
        state.employeeDetailsLoading = false;
        state.employeeDetailsError =
          (action.payload as string) || "Failed to fetch employee details";
      })
      .addCase(saveTuitionEmployeeDetailsAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        saveTuitionEmployeeDetailsAdmin.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.employeeDetails = {
            ...state.employeeDetails,
            ...action.payload,
          };
        },
      )
      .addCase(
        saveTuitionEmployeeDetailsAdmin.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || null;
        },
      )
      // .addCase(fetchClassEligibility.pending, (state) => {
      //   state.loading = true;
      //   state.error = null;
      // })
      .addCase(
        fetchClassEligibilityAdmin.fulfilled,
        (
          state,
          action: PayloadAction<{ isEligible: boolean; reason: string | null }>,
        ) => {
          state.loading = false;
          state.isEligible = action.payload.isEligible;
          state.eligibilityReason = action.payload.reason;
        },
      )
      .addCase(
        fetchClassEligibilityAdmin.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || null;
        },
      )
      .addCase(fetchMilesDataAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMilesDataAdmin.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(
        fetchMilesDataAdmin.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || null;
        },
      )
      .addCase(updateManagerDetailsAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateManagerDetailsAdmin.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.employeeDetails = {
            ...state.employeeDetails,
            ...action.payload,
          };
        },
      )
      .addCase(
        updateManagerDetailsAdmin.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || null;
        },
      )
      // Print Application PDF
      .addCase(downloadPrintApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadPrintApplication.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(downloadPrintApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Print Fax Cover PDF
      .addCase(downloadPrintFaxCover.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadPrintFaxCover.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(downloadPrintFaxCover.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    // ...existing code...
  },
});

export const {
  clearTuitionAdminDocuments,
  clearTuitionAdminEmails,
  clearTuitionAdminEmailDetails,
  clearApplications,
  clearAdminError,
  setCurrentPage,
} = tuitionAdminSlice.actions;
export default tuitionAdminSlice.reducer;
